<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#app';
import { useThanksData } from '@/composables/useThanksData';
import { useRequest } from '@/composables/useRequest';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
import { useGlobalStore } from '@/stores/global';
import { createConsultationWideSchema } from '@/configs/validation-schemas';

const props = defineProps({
  type: { type: String, default: () => '' },
});

const { t } = useI18n();
const thanksData = useThanksData();
const globalStore = useGlobalStore();
const router = useRouter();
const route = useRoute();
const formType = computed(() => props.type || '');

const consultationWideSchema = computed(() => createConsultationWideSchema(t));
const loading = ref(false);
const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: consultationWideSchema,
  initialValues: {
    name: '',
    phone: '',
    question: '',
    page: '',
  },
});

const name = useField('name');
const phone = useField('phone');
const question = useField('question');

const phoneMaskRegExp = /^\+380 \d{2} \d{3} \d{4}$/;
const isRequiredFilled = computed(() => {
  if (!phoneMaskRegExp.test(phone.value.value || '')) return false;

  return consultationWideSchema.value.isValidSync({
    name: name.value.value,
    phone: phone.value.value,
    question: question.value.value,
  });
});

const formResponse = ref({});
const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, {
          action: formType.value || 'submit_form',
        })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};
const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  try {
    const token = await getCaptchaToken();
    const res = await useRequest(`/forms/${formApi[formType.value]}`, {
      method: 'POST',
      body: {
        name: values.name,
        phone: values.phone,
        question: values.question,
        page: currentFullUrl,
        token: token ? token : {},
      },
    });

    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName =
          t('application_popup.application_block_before_footer') +
          formType.value ? t(`application_popup.${formType.value}_title`) : '';
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: formType.value,
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }
      resetForm();
      thanksData.value = res;

      const langPrefix = route.params.lang ? `/${route.params.lang}` : '';
      router.push(`${langPrefix}/thanks`);
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <form class="form-application" @submit.prevent="onSubmit">
    <template v-if="formResponse && formResponse?.header">
      <div class="form-subscription__thanks">
        <p class="mb-0 h5">
          {{ formResponse.header || $t('thanks.subscription_success_header') }}
        </p>
        <p v-if="formResponse.content" class="mb-0 body-1-regular thanks__text">
          {{ formResponse.content || $t('thanks.subscription_success_text') }}
        </p>
      </div>
    </template>
    <template v-else>
      <div
        class="form-application__item"
        :class="{ 'form-application__item--error': !!name.errorMessage.value }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <InputText
            id="applicationName"
            v-model="name.value.value"
            class="form-application__item-field input-transparent"
            :invalid="!!name.errorMessage.value"
            type="text"
          />
          <label
            for="applicationName"
            class="form-application__item-label"
            :class="{ 'error-100': !!name.errorMessage.value }"
          >
            {{ $t('application.first_name') }}*
          </label>
        </FloatLabel>
      </div>
      <div
        class="form-application__item"
        :class="{ 'form-application__item--error': !!phone.errorMessage.value }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <InputMask
            id="applicationPhone"
            v-model="phone.value.value"
            class="form-application__item-field input-transparent"
            mask="+380 99 999 9999"
            placeholder="+380"
            fluid
            :invalid="!!phone.errorMessage.value"
          />
          <label
            for="applicationPhone"
            class="form-application__item-label"
            :class="{ 'error-100': !!phone.errorMessage.value }"
          >
            {{ $t('application.phone') }}*
          </label>
        </FloatLabel>
      </div>
      <div
        class="form-application__item"
        :class="{
          'form-application__item--error': !!question.errorMessage.value,
        }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <Textarea
            id="applicationQuestion"
            v-model="question.value.value"
            class="form-application__item-field textarea-transparent"
            :invalid="!!question.errorMessage.value"
            rows="1"
            cols="30"
            auto-resize
          />
          <label
            for="applicationQuestion"
            class="form-application__item-label"
            :class="{ 'error-100': !!question.errorMessage.value }"
          >
            {{ $t('application.question') }}
          </label>
        </FloatLabel>
      </div>
      <Button
        :label="
          type === 'feedback'
            ? $t('application.send')
            : $t('application.send_button')
        "
        type="submit"
        class="form-application__button button-large-danger link-large"
        :loading="loading"
        :disabled="!isRequiredFilled"
        severity="danger"
        size="large"
      />
      <slot></slot>
    </template>
  </form>
</template>

<style scoped lang="scss">
.form-application {
  &__button {
    min-height: 80px;
    padding: 12px 32px;
    border-radius: 0 0 4px 4px;
    @include media-breakpoint-up(sm) {
      min-height: 98px;
    }
    @include media-breakpoint-up(md) {
      padding: 4px 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-bottom, 4);
      @include responsive-spacing(padding-left, 32);
      @include responsive-spacing(padding-right, 32);
      @include responsive-spacing(padding-top, 4);
      @include responsive-spacing(border-bottom-left-radius, 4);
      @include responsive-spacing(border-bottom-right-radius, 4);
      @include responsive-spacing(min-height, 98);
    }
  }
}
</style>
